import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import {
  AddSignatureRole,
  CheckSheetLoad,
  CheckSheetLoadSuccess,
  CompanyLoad,
  CompanyLoadSuccess,
  CompanySignatureTypeLoadSuccess,
  DeleteSignatureRole,
  McSiteLoad,
  OptionsActionTypes,
  PhaseLoad,
  PhaseLoadSuccess,
  SignatureRoleLoad,
  SignatureRoleLoadSuccess,
  SiteLoad,
  SiteLoadSuccess,
  UpdateCompany,
  UpdateSignatureRole,
  AssignmentRoleLoad,
  AssignmentRoleLoadSuccess,
  AddAssignmentRole,
  UpdateAssignmentRole,
  DeleteAssignmentRole,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { OptionsService } from '@completion/services';
import { CheckSheet, Company, Phase, Project, SignatureRole, Site, AssignmentRole } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class OptionsEffects {
  constructor(
    private actions$: Actions,
    private optionsService: OptionsService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SiteLoad>(OptionsActionTypes.SiteLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<Site[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getSites',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (res) => new SiteLoadSuccess(res),
      }),
    ),
  );

  loadMcSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType<McSiteLoad>(OptionsActionTypes.McSiteLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<Site[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getMcSites',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (res) => new SiteLoadSuccess(res),
      }),
    ),
  );

  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CompanyLoad>(OptionsActionTypes.CompanyLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<Company[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getCompanies',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.category],
        onSuccess: (res) => new CompanyLoadSuccess(res),
      }),
    ),
  );

  loadPhases$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PhaseLoad>(OptionsActionTypes.PhaseLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<Phase[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getPhases',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (res) => new PhaseLoadSuccess(res),
      }),
    ),
  );

  loadCheckSheets$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckSheetLoad>(OptionsActionTypes.CheckSheetLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CheckSheet[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getCheckSheets',
        serviceArgs: ([action, currentProject]) => [action.csType, currentProject.id],
        onSuccess: (res) => new CheckSheetLoadSuccess(res),
      }),
    ),
  );

  loadSignatureRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SignatureRoleLoad>(OptionsActionTypes.SignatureRoleLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<SignatureRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getSignatureRoles',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (res) => new SignatureRoleLoadSuccess(res),
      }),
    ),
  );

  loadAssignmentRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AssignmentRoleLoad>(OptionsActionTypes.AssignmentRoleLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<AssignmentRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getAssignmentRoles',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (res) => new AssignmentRoleLoadSuccess(res),
      }),
    ),
  );

  updateSignatureRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateSignatureRole>(OptionsActionTypes.UpdateSignatureRole),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<SignatureRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'updateSignatureRole',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.signatureRole],
        onSuccess: (res) => new SignatureRoleLoadSuccess(res),
      }),
    ),
  );

  updateAssignmentRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateAssignmentRole>(OptionsActionTypes.UpdateAssignmentRole),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<AssignmentRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'updateAssignmentRole',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.role],
        onSuccess: (res) => new AssignmentRoleLoadSuccess(res),
      }),
    ),
  );

  addSignatureRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddSignatureRole>(OptionsActionTypes.AddSignatureRole),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<SignatureRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'addSignatureRole',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.signatureRole],
        onSuccess: (res) => new SignatureRoleLoadSuccess(res),
      }),
    ),
  );

  addAssignmentRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddAssignmentRole>(OptionsActionTypes.AddAssignmentRole),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<AssignmentRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'addAssignmentRole',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.role],
        onSuccess: (res) => new AssignmentRoleLoadSuccess(res),
      }),
    ),
  );

  deleteSignatureRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteSignatureRole>(OptionsActionTypes.DeleteSignatureRole),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<SignatureRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'deleteSignatureRole',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.signatureRole.id],
        onSuccess: (res) => new SignatureRoleLoadSuccess(res),
      }),
    ),
  );

  deleteAssignmentRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteAssignmentRole>(OptionsActionTypes.DeleteAssignmentRole),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<AssignmentRole[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'deleteAssignmentRole',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.role.id],
        onSuccess: (res) => new AssignmentRoleLoadSuccess(res),
      }),
    ),
  );

  companySignatureTypeLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CompanyLoad>(OptionsActionTypes.CompanySignatureTypeLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<string[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'getCompanySignatureTypes',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (res) => new CompanySignatureTypeLoadSuccess(res),
      }),
    ),
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateCompany>(OptionsActionTypes.UpdateCompany),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<Company[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.optionsService,
        method: 'updateCompany',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.company],
        onSuccess: (res) => new CompanyLoadSuccess(res),
      }),
    ),
  );
}
